import React from 'react'
import { useLazyLoadQuery, graphql } from 'react-relay'
import { Trans, useLingui } from '@lingui/react/macro'
import { YStack, XStack, Separator } from 'tamagui'
import { lazy as loadable } from '@loadable/component'

import { Route, Routes, useLocation, useMatch } from 'react-router-dom'

import { useScreenMessages } from '@data/i18n'
import { Metadata } from '@newComponents/Metadata'

import { Screen } from '@newComponents/Screen'
import { H2, Text, Paragraph } from '@newComponents/Text'
import { Group } from '@newComponents/Group'
import { SponsorsSection } from '@newComponents/SponsorsSection'
import { Grid } from '@newComponents/Grid'
import { Card } from '@newComponents/Card'
import { wrapSuspenseComponent } from '@newComponents/SuspenseWrapper'
import { Link } from '@newComponents/Link'

import {
  GuildsScreenNewQuery,
  GuildsScreenNewQuery$data,
} from './__generated__/GuildsScreenNewQuery.graphql'

const GuildsScreenAll = wrapSuspenseComponent(
  loadable(() => import('../GuildsScreenAll/GuildsScreenAll'))
)

const GuildsScreenJoined = wrapSuspenseComponent(
  loadable(() => import('../GuildsScreenJoined/GuildsScreenJoined'))
)

export const GuildsScreenNew = () => {
  useScreenMessages((locale) => import(/* webpackChunkName: "locale-guilds-screen-[request]" */ `./locales/${locale}.po`))
  const { t } = useLingui()

  const data = useLazyLoadQuery<GuildsScreenNewQuery>(
    graphql`
      query GuildsScreenNewQuery {
        ...SponsorsSection_query

        viewer {
          id
        }
      }
    `,
    {}
  )

  const location = useLocation()

  const locationState = location.state as { backgroundLocation?: Location }

  const routes = (
    <Routes location={locationState?.backgroundLocation || location}>
      {!!data.viewer && (
        <Route path='joined' element={<GuildsScreenJoined />} />
      )}

      <Route path='*' element={<GuildsScreenAll />} />
    </Routes>
  )

  return (
    <>
      <Metadata title={t`Explore Guilds`} />

      <Screen gap='$8'>
        <YStack gap={100} $gtSm={{ display: 'none' }}>
          <YStack gap='$6'>
            <Header query={data} />

            {routes}
          </YStack>

          <Separator borderColor='$borderColor' paddingHorizontal='$4' />

          <SponsorsSection query={data} />
        </YStack>

        <Grid
          gap='$8'
          display='none'
          $gtSm={{ display: 'flex', minHeight: 650 }}
        >
          <Grid.Column
            gap='$6'
            flexGrow={1}
            $gtSm={{ flexGrow: 2 }}
            $gtLg={{ flexGrow: 3 }}
          >
            <Header query={data} />

            {routes}
          </Grid.Column>

          <Grid.Column gap='$4' flexGrow={1}>
            <YStack
              gap='$8'
              padding='$4'
              style={{ position: 'sticky', top: 152 }}
            >
              <YStack
                gap='$4'
                $gtMdHt={{ display: 'flex' }}
                $mdHt={{ display: 'none' }}
              >
                <Paragraph size='$4' color='$color'>
                  <Trans>Join a Guild to connect with other members, stay up to date with events, and watch interesting presentations</Trans> 🙌
                </Paragraph>

                <Paragraph size='$4' color='$color'>
                  <Trans>Thanks to our sponsors for making it possible</Trans> 👇
                </Paragraph>
              </YStack>

              <SponsorsSection query={data} />
            </YStack>
          </Grid.Column>
        </Grid>
      </Screen>
    </>
  )
}

const Header = ({ query }: { query: GuildsScreenNewQuery$data }) => {
  const joinedRouteMatch = useMatch('/guilds/joined')

  return (
    <XStack alignItems='center' justifyContent='space-between' minHeight={32}>
      <H2>
        {joinedRouteMatch && query.viewer ? <Trans>Joined Guilds</Trans> : <Trans>All Guilds</Trans>}
      </H2>

      {!!query.viewer && (
        <Group activeIndex={joinedRouteMatch ? 1 : 0}>
          <XStack gap='$2'>
            <Group.Item index={0}>
              <Link
                href='/guilds'
                size='$4'
                fontWeight='$6'
                color='$colorStrong'
                hoverStyle={{
                  textDecorationLine: 'none',
                }}
                paddingVertical='$2'
                paddingHorizontal='$3'
              >
                <Trans>All</Trans>
              </Link>
            </Group.Item>

            <Group.Item index={1}>
              <Link
                href='/guilds/joined'
                size='$4'
                fontWeight='$6'
                color='$colorStrong'
                hoverStyle={{
                  textDecorationLine: 'none',
                }}
                paddingVertical='$2'
                paddingHorizontal='$3'
              >
                <Trans>Joined</Trans>
              </Link>
            </Group.Item>
          </XStack>
        </Group>
      )}
    </XStack>
  )
}

export default GuildsScreenNew
